import { Icon, IconsEnum } from 'components/Icons';
import { InputHTMLAttributes } from 'react';
import { Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { isControlledInput } from '../utils';
import style from './ChipRadioButton.module.scss';

export type ChipOptionProps = {
  label: string;
  value: any; // EXPLICIT ANY TO PERMIT ALL VALUE, GENERIC IS NECESSARY?
};

export type ChipRadioButtonProps = {
  name: string;
  valueDefault?: string;
  isVertical?: boolean;
  options: Array<ChipOptionProps>;
  deselectionButtonFunction?: (ev: any) => void;
  valueChecked?: string;
  chipClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & InputHTMLAttributes<HTMLInputElement>;

export type ControlledChipRadioButtonProps<
  T extends FieldValues = FieldValues,
  K extends Path<T> = Path<T>
> = ChipRadioButtonProps & UseControllerProps<T, K>;

export function ChipRadioButton<T extends FieldValues>(props: ControlledChipRadioButtonProps<T>): JSX.Element;
export function ChipRadioButton(props: ChipRadioButtonProps): JSX.Element;

export function ChipRadioButton(props: ChipRadioButtonProps | ControlledChipRadioButtonProps): JSX.Element {
  const deselection = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    props.deselectionButtonFunction && props.deselectionButtonFunction(ev);
  };

  if (isControlledInput<ControlledChipRadioButtonProps>(props)) {
    const { control, rules, name, options, isVertical, chipClassName, ...otherProps } = props;
    return (
      <Controller
        key={`${name}_controller_chipRadioButton`}
        control={control}
        name={name}
        rules={rules}
        defaultValue={props.defaultValue}
        render={({ field: { name, onBlur, onChange, ref, ...field } }) => (
          <div className={`${style.container} ${isVertical && style.vertical} ${otherProps.className}`}>
            {options.map(({ label, value }, index) => {
              return (
                <div
                  className={`${style.inputContainer} ${chipClassName ? chipClassName : ''}`}
                  key={`buttonContainer_${name}_${index}`}
                >
                  <input
                    {...otherProps}
                    name={name}
                    id={`${name}_${index}`}
                    key={`${name}_${index}`}
                    type="radio"
                    value={value}
                    ref={ref}
                    checked={field.value === value}
                    onChange={(e) => {
                      onChange(value);
                      props.onChange && props.onChange(e);
                    }}
                    onBlur={(e) => {
                      onBlur();
                      props.onBlur && props.onBlur(e);
                    }}
                  />
                  <label htmlFor={`${name}_${index}`}>{label}</label>
                </div>
              );
            })}
          </div>
        )}
      />
    );
  }
  return (
    <div className={`${style.container} ${props.isVertical && style.vertical}`}>
      {props.options.map(({ label, value }, index) => (
        <div
          className={`${style.inputContainer} ${props.chipClassName ? props.chipClassName : ''}`}
          key={`buttonContainer_${props.name}_${index}`}
        >
          <input
            id={`${value}`}
            key={`${value}`}
            type="radio"
            value={value}
            name={props.name}
            checked={props.valueChecked === value}
            disabled={props.disabled}
            onChange={(e) => {
              props.onChange && props.onChange(e);
            }}
            onBlur={(e) => {
              props.onBlur && props.onBlur(e);
            }}
          />
          <label htmlFor={`${value}`}>
            {label}
            {props.deselectionButtonFunction && props.valueChecked === value && (
              <button className={style.deselectionButton} onClick={(ev) => deselection(ev)}>
                <Icon iconId={IconsEnum.CLOSE} />
              </button>
            )}
          </label>
        </div>
      ))}
    </div>
  );
}
